import { AxleDialogShellBehaviorProps } from '@/dialogs/AxleDialogShell/AxleDialogShell.types';
import { FeatureFlag } from '@/types/featureFlag';
import { useFeatureFlagsEnabled } from '@/utils/useFeatureFlagEnabled';

// Module-level so the queried key list keeps a stable identity across renders.
export const DIALOG_SHELL_FLAGS = {
    isRepositioningEnabled: FeatureFlag.DASHBOARD_DIALOG_REPOSITIONING,
};

/* The shell behavior the flags resolve to.
 *
 * Pure, so a caller that already reads other flags can add these keys to its own query and
 * derive the behavior from the same result rather than issuing a second one.
 */
export const toDialogShellBehavior = (
    // Keyed by the names in DIALOG_SHELL_FLAGS, which is how the flags hook reports results.
    enabled: Record<string, boolean>,
): AxleDialogShellBehaviorProps => ({
    isDraggable: enabled.isRepositioningEnabled,
    resetPositionOnDoubleClick: enabled.isRepositioningEnabled,
});

/* Resolves the dialog shell's interaction behavior from feature flags.
 *
 * Belongs to whatever decides to render a shell dialog, not to the dialog itself: the flags
 * read needs an authenticated session, so a dialog that resolved its own behavior could only be
 * exercised in Storybook by faking auth.
 *
 * Suspends rather than reporting a loading state, so the dialog paints once with the behavior it
 * keeps. The non-suspending variant would render it undraggable for a beat and then swap the
 * handlers in underneath whoever was already reaching for the header.
 */
export const useDialogShellFlags = (): AxleDialogShellBehaviorProps =>
    toDialogShellBehavior(useFeatureFlagsEnabled(DIALOG_SHELL_FLAGS));
